/**
 * Digital Payment Service
 * Handles mobile app payment operations and integration with accounting system
 */

import { supabase } from '@/lib/supabase';

export interface DigitalPayment {
  id: string;
  church_id: string;
  member_id: string;
  amount: number;
  currency: string;
  payment_method: 'momo' | 'card' | 'bank_transfer';
  gateway: 'paystack' | 'flutterwave' | 'momo_api';
  gateway_reference: string;
  gateway_transaction_id?: string;
  gateway_fee: number;
  net_amount: number;
  payment_type: 'tithe' | 'offering' | 'pledge' | 'building_fund' | 'welfare' | 'missions';
  revenue_category_id?: string;
  account_id?: string;
  pledge_id?: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  is_recorded_in_ledger: boolean;
  journal_entry_id?: string;
  recorded_at?: string;
  description?: string;
  payment_date: string;
  completed_at?: string;
  app_version?: string;
  device_type?: 'android' | 'ios';
  created_at: string;
  updated_at: string;
}

export interface DigitalPaymentWithMember extends DigitalPayment {
  members?: {
    preferred_name?: string;
    member_number?: string;
    email?: string;
    phone_number?: string;
  };
  revenue_categories?: {
    name: string;
  };
  chart_of_accounts?: {
    account_name: string;
    account_code: string;
  };
}

export interface InitiatePaymentRequest {
  member_id: string;
  amount: number;
  payment_type: string;
  revenue_category_id?: string;
  description?: string;
  pledge_id?: string;
}

export interface PaymentStats {
  total_amount: number;
  total_count: number;
  total_fees: number;
  net_amount: number;
  by_type: Record<string, { count: number; amount: number }>;
  by_status: Record<string, number>;
  completed_count: number;
  pending_count: number;
  failed_count: number;
}

class DigitalPaymentService {
  /**
   * Get all digital payments for a church
   */
  async getChurchPayments(
    churchId: string,
    filters?: {
      startDate?: string;
      endDate?: string;
      status?: string;
      payment_type?: string;
      member_id?: string;
    }
  ): Promise<DigitalPaymentWithMember[]> {
    try {
      let query = supabase
        .from('digital_payments')
        .select(`
          *,
          members(preferred_name, member_number, email, phone_number),
          revenue_categories(name),
          chart_of_accounts(account_name, account_code)
        `)
        .eq('church_id', churchId);

      if (filters?.startDate) {
        query = query.gte('payment_date', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('payment_date', filters.endDate);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.payment_type) {
        query = query.eq('payment_type', filters.payment_type);
      }
      if (filters?.member_id) {
        query = query.eq('member_id', filters.member_id);
      }

      const { data, error } = await query.order('payment_date', { ascending: false });

      if (error) {
        console.error('Error fetching church payments:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getChurchPayments:', error);
      return [];
    }
  }

  /**
   * Get member's payment history
   */
  async getMemberPayments(memberId: string): Promise<DigitalPayment[]> {
    try {
      const { data, error } = await supabase
        .from('digital_payments')
        .select('*')
        .eq('member_id', memberId)
        .order('payment_date', { ascending: false });

      if (error) {
        console.error('Error fetching member payments:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getMemberPayments:', error);
      return [];
    }
  }

  /**
   * Get payment statistics for a period
   */
  async getPaymentStats(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<PaymentStats> {
    try {
      const { data: payments, error } = await supabase
        .from('digital_payments')
        .select('*')
        .eq('church_id', churchId)
        .gte('payment_date', startDate)
        .lte('payment_date', endDate);

      if (error) {
        console.error('Error fetching payment stats:', error);
        throw error;
      }

      const stats: PaymentStats = {
        total_amount: 0,
        total_count: payments?.length || 0,
        total_fees: 0,
        net_amount: 0,
        by_type: {},
        by_status: {},
        completed_count: 0,
        pending_count: 0,
        failed_count: 0
      };

      payments?.forEach(payment => {
        stats.total_amount += payment.amount;
        stats.total_fees += payment.gateway_fee;
        stats.net_amount += payment.net_amount;

        // Count by type
        if (!stats.by_type[payment.payment_type]) {
          stats.by_type[payment.payment_type] = { count: 0, amount: 0 };
        }
        stats.by_type[payment.payment_type].count += 1;
        stats.by_type[payment.payment_type].amount += payment.amount;

        // Count by status
        stats.by_status[payment.status] = (stats.by_status[payment.status] || 0) + 1;

        if (payment.status === 'completed') stats.completed_count += 1;
        if (payment.status === 'pending') stats.pending_count += 1;
        if (payment.status === 'failed') stats.failed_count += 1;
      });

      return stats;
    } catch (error) {
      console.error('Error in getPaymentStats:', error);
      return {
        total_amount: 0,
        total_count: 0,
        total_fees: 0,
        net_amount: 0,
        by_type: {},
        by_status: {},
        completed_count: 0,
        pending_count: 0,
        failed_count: 0
      };
    }
  }

  /**
   * Get single payment by ID
   */
  async getPaymentById(paymentId: string): Promise<DigitalPaymentWithMember | null> {
    try {
      const { data, error } = await supabase
        .from('digital_payments')
        .select(`
          *,
          members(preferred_name, member_number, email, phone_number),
          revenue_categories(name),
          chart_of_accounts(account_name, account_code)
        `)
        .eq('id', paymentId)
        .single();

      if (error) {
        console.error('Error fetching payment:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getPaymentById:', error);
      return null;
    }
  }

  /**
   * Get payment by gateway reference
   */
  async getPaymentByReference(reference: string): Promise<DigitalPayment | null> {
    try {
      const { data, error } = await supabase
        .from('digital_payments')
        .select('*')
        .eq('gateway_reference', reference)
        .single();

      if (error) {
        console.error('Error fetching payment by reference:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getPaymentByReference:', error);
      return null;
    }
  }

  /**
   * Initiate a payment (for admin creating payment on behalf of member)
   */
  async createPayment(payment: Partial<DigitalPayment>): Promise<{ success: boolean; data?: DigitalPayment; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('digital_payments')
        .insert({
          ...payment,
          gateway_reference: payment.gateway_reference || `MANUAL-${Date.now()}`,
          status: payment.status || 'pending',
          payment_date: payment.payment_date || new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating payment:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('Error in createPayment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update payment status (typically called by webhook)
   */
  async updatePaymentStatus(
    paymentId: string,
    updates: {
      status?: string;
      gateway_transaction_id?: string;
      gateway_fee?: number;
      completed_at?: string;
      payment_method?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('digital_payments')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) {
        console.error('Error updating payment:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error in updatePaymentStatus:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get payments not yet recorded in ledger
   */
  async getUnrecordedPayments(churchId: string): Promise<DigitalPayment[]> {
    try {
      const { data, error } = await supabase
        .from('digital_payments')
        .select('*')
        .eq('church_id', churchId)
        .eq('status', 'completed')
        .eq('is_recorded_in_ledger', false)
        .order('payment_date', { ascending: false });

      if (error) {
        console.error('Error fetching unrecorded payments:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUnrecordedPayments:', error);
      return [];
    }
  }

  /**
   * Manually trigger journal entry creation for a payment
   * (In case auto-trigger failed)
   */
  async recordPaymentInLedger(paymentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Simply update the status to trigger the database function
      const { error } = await supabase
        .from('digital_payments')
        .update({
          status: 'completed', // Re-set to trigger the function
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) {
        console.error('Error recording payment in ledger:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error in recordPaymentInLedger:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get recent payments (for dashboard)
   */
  async getRecentPayments(churchId: string, limit: number = 10): Promise<DigitalPaymentWithMember[]> {
    try {
      const { data, error } = await supabase
        .from('digital_payments')
        .select(`
          *,
          members(preferred_name, member_number, email),
          revenue_categories(name)
        `)
        .eq('church_id', churchId)
        .order('payment_date', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent payments:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getRecentPayments:', error);
      return [];
    }
  }

  /**
   * Export payments to CSV
   */
  exportToCSV(payments: DigitalPaymentWithMember[]): string {
    const headers = [
      'Date',
      'Member',
      'Type',
      'Amount',
      'Fee',
      'Net Amount',
      'Gateway',
      'Reference',
      'Status',
      'Recorded in Ledger'
    ];

    const rows = payments.map(p => [
      new Date(p.payment_date).toLocaleDateString(),
      p.members ? (p.members.preferred_name || p.members.email || p.members.member_number || 'Unknown') : 'Unknown',
      p.payment_type,
      p.amount.toFixed(2),
      p.gateway_fee.toFixed(2),
      p.net_amount.toFixed(2),
      p.gateway,
      p.gateway_reference,
      p.status,
      p.is_recorded_in_ledger ? 'Yes' : 'No'
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return csv;
  }
}

export const digitalPaymentService = new DigitalPaymentService();
